<template>
  <div class="row">
    <div class="col-2">
      <span class="custom-firmname">{{ opportunity.sourceFirmName }}</span>
      <div>
        <button v-if="opportunity.isViewable" class="btn btn-primary btn-sm mt-2" @click="openNotes">Notes</button>
      </div>
    </div>
    <div class="col">
      <table class="custom-table">
        <thead>
          <tr class="custom-table-header">
            <th>#</th>
            <th class="d-none">Status</th>
            <th>Inspection #</th>
            <th>RO #</th>
            <th>Estimator</th>
            <th>Insurance</th>
            <th>Technician</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ opportunity.opportunity }}</td>
            <td>
              <select v-if="opportunity.statusName !== 'Pending'" v-model="opportunity.status"
                class="d-none form-control custom-select" @change="updateStatus">
                <option v-for="status in statuses" :key="status.id" :value="status.id">{{ status.name }}</option>
              </select>
              <span v-if="isCompleted" class="custom-completed">on {{ completedDate }}</span>
            </td>
            <td>{{ opportunity.adasInspection }}</td>
            <td>
              {{ opportunity.ronumber }}
              <!-- Issue 622 (5f): flag inspections created from an uploaded PDF; link downloads it. -->
              <div v-if="opportunity.wasPdf" class="small mt-1">
                <a v-if="opportunity.repairDocument" href="#" @click.prevent="downloadPdf" title="Download the uploaded PDF estimate">
                  Created from PDF <i class="fas fa-download"></i>
                </a>
                <span v-else class="text-muted">Created from PDF</span>
              </div>
            </td>
            <td>{{ opportunity.estimator }}</td>
            <td>{{ opportunity.insurance }}</td>
            <td>{{ opportunity.techs }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="hasShopSchedule && !isCancelled" class="custom-shop-schedule mt-2">
        <span class="custom-schedule-label">Shop Requested Schedule:</span>
        <span class="custom-schedule-value">{{ shopScheduleDisplay }}</span>
      </div>
    </div>
  </div>
  <DialogRequestNotes v-model="actionNotes" @close="closeModal" />
</template>
<script>
import { mapState, mapActions } from 'pinia'
import axios from 'axios'
import { authStore } from '@/stores/auth'
import { lookupStore } from '@/stores/lookup'
import { opportunityStore } from '@/stores/opportunity'
import globals from '@/plugins/globals'
import DialogRequestNotes from '@/components/opportunity/forms/DialogRequestNotes.vue'
export default {
  name: 'OpportunityDetail',
  components: {
    DialogRequestNotes
  },
  props: {
    opportunity: Object
  },
  data() {
    return {
      actionNotes: null
    }
  },
  mounted() {
    this.actionNotes = null
  },
  computed: {
    ...mapState(authStore, ['profile', 'token']),
    ...mapState(lookupStore, ['lookups']),
    statuses() {
      let _data = []
      if (this.lookups['Status']) {
        const ids = [1, 2, 3, 4, 5, 6, 8, 9, 10, 11]
        _data = this.lookups['Status'].filter((item) => ids.includes(item.id))
        _data = globals.sortArray(_data, 'name')
      }
      return _data
    },
    isCompleted() {
      return this.opportunity.completed !== null
    },
    isCancelled() {
      return this.opportunity.statusName === 'Cancelled'
    },
    completedDate() {
      let _date = ''
      if (this.opportunity.completed !== null) {
        _date = globals.formatDate(this.opportunity.completed)
      }
      return _date
    },
    hasShopSchedule() {
      return this.opportunity.scheduled !== null && this.opportunity.preferredTimeOfDay !== null
    },
    shopScheduleDisplay() {
      if (!this.hasShopSchedule) return ''
      if (!this.opportunity.scheduled) return ''
      const date = globals.formatDate(this.opportunity.scheduled, 'date')
      const timeOfDay = this.opportunity.preferredTimeOfDay
      if (timeOfDay !== undefined) {
        return `${date} (${timeOfDay})`
      } else {
        return `${date}`
      }
    }
  },
  methods: {
    ...mapActions(opportunityStore, ['setOpportunity', 'saveStatus', 'getRequestNotes']),
    // Issue 622 (5f): download the source PDF via the CCP proxy (forwards to the owning ADAS Map instance).
    async downloadPdf() {
      if (!this.opportunity || !this.opportunity.repairDocument) return
      try {
        const apiUrl = window.config ? window.config.apiUrl : ''
        const resp = await axios.get(`${apiUrl}/estimate/document/${this.opportunity.repairDocument}/download`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${this.token}` }
        })
        const url = URL.createObjectURL(resp.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `estimate-${this.opportunity.repairDocument}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('downloadPdf failed', e)
      }
    },
    closeModal() {
      this.actionNotes = null
    },
    async openNotes() {
      console.log('openNotes', this.opportunity)
      this.setOpportunity({
        name: 'opportunity',
        value: this.opportunity
      })
      await this.getRequestNotes({
        calibrateRequest: this.opportunity.calibrateRequest,
        opportunity: this.opportunity.opportunity,
        readBy: `${this.profile.firstName} ${this.profile.lastName}`
      })
      this.actionNotes = true
    },
    async updateStatus() {
      let data = {
        opportunity: this.opportunity.opportunity,
        status: this.opportunity.status,
        statusName: this.statuses.find((item) => parseInt(item.id) === parseInt(this.opportunity.status)).name
      }
      await this.saveStatus(data)
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-ids-wrapper {
  position: relative;
}

.custom-firmname {
  font-weight: 700;
}

.custom-table {
  width: 100%;
}

.custom-table-header {
  th {
    font-style: italic;
    font-weight: 500;
  }
}

.custom-id {
  position: absolute;
  bottom: 0;
  right: 15px;
  font-size: 0.8em;
}

.custom-select {
  width: 140px;
}

.custom-completed {
  font-size: 0.8em;
  font-style: italic;
}

.custom-shop-schedule {
  padding: 8px 12px;
  background-color: #e8f4fd;
  border: 1px solid #b8daff;
  border-radius: 4px;
}

.custom-schedule-label {
  font-weight: 600;
  color: #004085;
  margin-right: 8px;
}

.custom-schedule-value {
  font-weight: 500;
  color: #004085;
}
</style>
